"use client"

import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { X, Maximize2, Minimize2, Copy, CheckCircle, FileText, BrainCircuit, Sparkles, Target, TrendingUp } from "lucide-react";
import { Button, Input, Textarea, useToast } from "@mantis-sas/ui";

export default function FlowModal({
  data,
  onClose,
  columnLabels = {},
  formatCellValue,
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [copied, setCopied] = useState(false);
  const [isRetrainingMode, setIsRetrainingMode] = useState(false);
  const [retrainingReason, setRetrainingReason] = useState("");
  const [isRetraining, setIsRetraining] = useState(false);
  const [retrainingComplete, setRetrainingComplete] = useState(false);
  const [accuracyBefore, setAccuracyBefore] = useState(0);
  const [accuracyAfter, setAccuracyAfter] = useState(0);
  const [retrainingProgress, setRetrainingProgress] = useState(0);
  const [retrainingStage, setRetrainingStage] = useState("");
  const [editedData, setEditedData] = useState({});
  const [hasEdits, setHasEdits] = useState(false);
  const retrainingStages = [
    "Analizando datos de entrenamiento...",
    "Preparando vectores de entrada...",
    "Ajustando parámetros del modelo...",
    "Optimizando capas neuronales...",
    "Validando resultados...",
    "Finalizando reentrenamiento..."
  ];
  const [selectedFileToPreview, setSelectedFileToPreview] = useState(null);
  const { toast } = useToast();
  const textareaRef = useRef(null);
  const filesToPreview = Object.entries(data)
    .filter(([key, value]) => value && key.toLowerCase().includes("url"))
    .map(([key, value]) => {
      return {
        name: key.replace("file_url_", "").replace("_", " "),
        url: value,
      };
    });
  console.log(filesToPreview);

  // Set initial selected file
  useEffect(() => {
    if (filesToPreview.length > 0 && !selectedFileToPreview) {
      setSelectedFileToPreview(filesToPreview[0]);
    }
  }, [filesToPreview, selectedFileToPreview]);

  // Ajustar altura de textarea automáticamente
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [retrainingReason]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const toggleRetrainingMode = () => {
    setIsRetrainingMode(!isRetrainingMode);
    if (!isRetrainingMode) {
      // Initialize editedData with current data values
      const initialEditedData = {};
      Object.entries(data)
        .filter(([key]) => key !== "id" && key !== "documentUrl")
        .forEach(([key, value]) => {
          initialEditedData[key] = value;
        });
      setEditedData(initialEditedData);
      
      // Reset retraining state when entering retraining mode
      setRetrainingReason("");
      setRetrainingComplete(false);
      setRetrainingProgress(0);
      setRetrainingStage("");
      setHasEdits(false);
    }
  };

  const handleFieldEdit = (key, value) => {
    setEditedData(prev => ({ ...prev, [key]: value }));
    setHasEdits(true);
  };

  const startRetraining = () => {
    if (!hasEdits) {
      toast({
        title: "Error",
        description: "No se han realizado cambios para reentrenar",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    // Automatically generate retraining reason based on edited fields
    const changedFields = Object.entries(editedData)
      .filter(([key, value]) => data[key] !== value)
      .map(([key]) => getColumnLabel(key))
      .join(", ");
    
    setRetrainingReason(`Corrección de datos en los campos: ${changedFields}`);
    setIsRetraining(true);
    setRetrainingProgress(0);

    // Simular progreso del reentrenamiento con etapas
    let stageIndex = 0;
    let progressValue = 0;
    const progressInterval = setInterval(() => {
      progressValue += 2;

      if (progressValue >= 100) {
        clearInterval(progressInterval);
        return;
      }

      // Cambiar de etapa cada ~20% de progreso
      if (
        progressValue % 20 === 0 &&
        stageIndex < retrainingStages.length - 1
      ) {
        stageIndex++;
        setRetrainingStage(retrainingStages[stageIndex]);
      }

      setRetrainingProgress(progressValue);
    }, 60);

    // Iniciar la primera etapa
    setRetrainingStage(retrainingStages[0]);

    // Simulación de proceso de reentrenamiento
    // En un caso real, aquí iría la llamada a la API para reentrenar el modelo
    setTimeout(() => {
      // Valores simulados de precisión
      const previousAccuracy = 78.5;
      const newAccuracy = 92.3;

      clearInterval(progressInterval);
      setRetrainingProgress(100);
      setRetrainingStage(retrainingStages[retrainingStages.length - 1]);

      setTimeout(() => {
        setAccuracyBefore(previousAccuracy);
        setAccuracyAfter(newAccuracy);
        setIsRetraining(false);
        setRetrainingComplete(true);

        toast({
          title: "✨ Reentrenamiento completado",
          description: `La precisión ha mejorado de ${previousAccuracy}% a ${newAccuracy}%`,
          duration: 5000,
        });
      }, 500);
    }, 6000);
  };

  const copyToClipboard = () => {
    const textToCopy = Object.entries(data)
      .filter(([key]) => key !== "id" && key !== "documentUrl")
      .map(([key, value]) => `${key}: ${value}`)
      .join("\n");

    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      toast({
        title: "Copied to clipboard",
        duration: 2000,
      });
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const highlightText = (text, searchTerm) => {
    if (!searchTerm || typeof text !== "string") return text;

    const parts = text.split(new RegExp(`(${searchTerm})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={i} className="bg-yellow-200 dark:bg-yellow-700">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  // Obtener el nombre formateado de la columna o usar el nombre original
  const getColumnLabel = (column) => {
    return columnLabels[column] || column;
  };

  const handleSaveChanges = () => {
    if (!hasEdits) {
      toast({
        title: "No hay cambios",
        description: "No se han realizado cambios para guardar",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    
    // Here you would add the API call to save the changes without retraining
    // For now we'll just simulate it with a toast
    
    toast({
      title: "Cambios guardados",
      description: "Los cambios se han guardado correctamente",
      duration: 3000,
    });
    
    // After successful save, we would update the original data
    // In a real implementation, this would happen after API success
    Object.entries(editedData).forEach(([key, value]) => {
      data[key] = value;
    });
    
    setHasEdits(false);
    setIsRetrainingMode(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
          isFullscreen ? "bg-black" : "bg-black/50 backdrop-blur-sm"
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <style jsx global>{`
          /* Custom scrollbar styling */
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background-color: rgba(156, 163, 175, 0.5);
            border-radius: 20px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background-color: rgba(156, 163, 175, 0.7);
          }
          /* For Firefox */
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
          }

          /* Dark mode adjustments */
          .dark .custom-scrollbar::-webkit-scrollbar-thumb {
            background-color: rgba(71, 85, 105, 0.5);
          }
          .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background-color: rgba(71, 85, 105, 0.7);
          }
          .dark .custom-scrollbar {
            scrollbar-color: rgba(71, 85, 105, 0.5) transparent;
          }
        `}</style>

        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.9 }}
          className={`bg-white dark:bg-slate-800 shadow-xl overflow-hidden ${
            isFullscreen
              ? "fixed inset-0 m-0 border-t-0"
              : "max-w-6xl w-full max-h-[90vh] rounded border-t-0"
          } border border-slate-200/60 dark:border-slate-700/60`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-3 sm:p-4 border-b border-slate-200/70 dark:border-slate-700/70">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              Detalles del proceso
            </h3>
            <div className="flex items-center space-x-2">
              {hasEdits && (
                <>
                  <Button
                    onClick={handleSaveChanges}
                    className="mr-1 bg-slate-100 hover:bg-slate-200 text-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200"
                    size="sm"
                  >
                    <CheckCircle className="h-3.5 w-3.5 mr-1" /> Guardar
                  </Button>
                  <Button
                    onClick={startRetraining}
                    className="mr-1 bg-gradient-to-br from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white shadow-sm shadow-orange-500/20 border-0"
                    size="sm"
                  >
                    <BrainCircuit className="h-3.5 w-3.5 mr-1" /> Entrenar
                  </Button>
                </>
              )}
              {/* Botón de reentrenamiento con tooltip */}
              <div className="group relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleRetrainingMode}
                  className={`rounded-full text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 ${
                    isRetrainingMode
                      ? "bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400"
                      : ""
                  }`}
                >
                  <BrainCircuit className="h-4 w-4" />
                </Button>
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg">
                  {isRetrainingMode ? "Salir del modo IA" : "Reentrenar IA"}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleFullscreen}
                className="rounded-full text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row h-[calc(100%-3.5rem)]">
            {/* Document Preview */}
            <div className="w-full max-h-full md:w-1/2 overflow-hidden flex flex-col border-r border-slate-200 dark:border-slate-700">
              {/* File tabs */}
              <div className="flex overflow-x-auto border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                {filesToPreview.map((file, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedFileToPreview(file)}
                    className={`
                      px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2
                      rounded-full hover:bg-white/60 dark:hover:bg-slate-800/60
                      ${
                        selectedFileToPreview?.url === file.url
                          ? "bg-white dark:bg-slate-800 text-orange-600 dark:text-orange-400 shadow-sm"
                          : "text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                      }
                    `}
                  >
                    <FileText className="h-4 w-4" />
                    {file.name + ".pdf" || `Archivo ${index + 1}`}
                  </button>
                ))}
              </div>

              {/* Preview content */}
              <div className="flex-1 overflow-auto">
                {selectedFileToPreview?.url ? (
                  <>
                    {selectedFileToPreview.url.includes(".pdf") ? (
                      <iframe
                        src={selectedFileToPreview.url}
                        className="w-full h-full"
                        title="Documento PDF"
                        style={{ border: "none" }}
                      />
                    ) : selectedFileToPreview.url.includes(".xls") ||
                      selectedFileToPreview.url.includes(".xlsx") ? (
                      <iframe
                        src={`https://view.officeapps.live.com/op/view.aspx?src=${selectedFileToPreview.url}`}
                        className="w-full h-full"
                        title="Documento Excel"
                        style={{ border: "none" }}
                      />
                    ) : selectedFileToPreview.url.includes(".jpeg") ||
                      selectedFileToPreview.url.includes(".jpg") ||
                      selectedFileToPreview.url.includes(".png") ? (
                      <img
                        src={selectedFileToPreview.url}
                        alt="imagen"
                        className="max-w-full h-auto"
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center space-y-4 p-8">
                          <FileText className="h-16 w-16 mx-auto text-slate-300 dark:text-slate-600" />
                          <p className="text-lg font-medium text-slate-600 dark:text-slate-300">
                            No hay documento disponible
                          </p>
                          <p className="text-sm text-slate-400 dark:text-slate-500">
                            URL no encontrada
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center space-y-4 p-8">
                      <FileText className="h-16 w-16 mx-auto text-slate-300 dark:text-slate-600" />
                      <p className="text-lg font-medium text-slate-600 dark:text-slate-300">
                        No hay documentos disponibles
                      </p>
                      <p className="text-sm text-slate-400 dark:text-slate-500">
                        No se encontraron archivos para previsualizar
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Extracted Data or Retraining View */}
            <div className="w-full md:w-1/2 h-[300px] md:h-full overflow-auto custom-scrollbar">
              {isRetrainingMode && (isRetraining || retrainingComplete) ? (
                <div className="p-6 h-full flex flex-col">
                  <div className="flex items-center space-x-3 mb-5">
                    <BrainCircuit className="h-6 w-6 text-orange-500" />
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                      Reentrenamiento de IA
                    </h3>
                  </div>

                  <AnimatePresence mode="wait">
                    {retrainingComplete ? (
                      <motion.div
                        key="complete"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="space-y-6 flex-1"
                      >
                        <motion.div
                          initial={{ scale: 0.9 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.5 }}
                          className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/10 rounded p-6 border border-green-200/80 dark:border-green-800/50 relative overflow-hidden shadow-sm"
                        >
                          <div className="absolute top-0 right-0 -mt-4 -mr-4">
                            <motion.div
                              initial={{ scale: 0, rotate: -45 }}
                              animate={{ scale: 1, rotate: 0 }}
                              transition={{ delay: 0.3, duration: 0.5 }}
                            >
                              <Sparkles className="h-10 w-10 text-green-500/50" />
                            </motion.div>
                          </div>

                          <div className="flex items-start space-x-4">
                            <div className="bg-white dark:bg-slate-800 rounded-full p-2.5 shadow-md">
                              <CheckCircle className="h-6 w-6 text-green-500" />
                            </div>

                            <div className="flex-1">
                              <h4 className="font-medium text-green-700 dark:text-green-400 text-lg mb-2">
                                ¡Reentrenamiento completado!
                              </h4>

                              <div className="space-y-4 mt-4">
                                <div>
                                  <div className="text-sm text-slate-500 dark:text-slate-400 mb-1.5">
                                    Precisión anterior
                                  </div>
                                  <div className="flex items-center">
                                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 mr-3">
                                      <motion.div
                                        initial={{ width: 0 }}
                                        animate={{
                                          width: `${accuracyBefore}%`,
                                        }}
                                        transition={{ duration: 1, delay: 0.2 }}
                                        className="bg-slate-400 dark:bg-slate-500 h-3 rounded-full"
                                      />
                                    </div>
                                    <span className="font-medium text-slate-700 dark:text-slate-300 w-14 text-right">
                                      {accuracyBefore}%
                                    </span>
                                  </div>
                                </div>

                                <div>
                                  <div className="text-sm text-slate-500 dark:text-slate-400 mb-1.5">
                                    Nueva precisión
                                  </div>
                                  <div className="flex items-center">
                                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 mr-3">
                                      <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${accuracyAfter}%` }}
                                        transition={{
                                          duration: 1.5,
                                          delay: 0.5,
                                        }}
                                        className="bg-green-500 dark:bg-green-400 h-3 rounded-full"
                                      />
                                    </div>
                                    <span className="font-medium text-green-600 dark:text-green-400 w-14 text-right">
                                      {accuracyAfter}%
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-center justify-between py-3 px-4 bg-green-50 dark:bg-green-900/20 rounded border border-green-100/70 dark:border-green-800/40 shadow-sm">
                                  <span className="text-slate-700 dark:text-slate-300 font-medium flex items-center">
                                    <TrendingUp className="h-4 w-4 mr-2 text-green-500" />
                                    Mejora conseguida:
                                  </span>
                                  <span className="font-bold text-green-600 dark:text-green-400">
                                    +
                                    {(accuracyAfter - accuracyBefore).toFixed(
                                      1
                                    )}
                                    %
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>

                        <div className="bg-slate-50 dark:bg-slate-800/50 rounded p-5 border border-slate-200/70 dark:border-slate-700/50 shadow-sm">
                          <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2.5 flex items-center">
                            <Target className="h-4 w-4 mr-2 text-orange-500" />
                            Motivo del reentrenamiento
                          </h4>
                          <p className="text-slate-600 dark:text-slate-300 italic">
                            "{retrainingReason}"
                          </p>
                        </div>

                        <div className="flex justify-end mt-auto">
                          <Button
                            onClick={toggleRetrainingMode}
                            className="bg-slate-100 hover:bg-slate-200 text-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 font-medium rounded-full px-5"
                          >
                            Volver a los datos
                          </Button>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="training"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="space-y-6 flex-1 flex flex-col"
                      >
                        {isRetraining ? (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex-1 flex flex-col"
                          >
                            <div className="text-center p-6 flex-1 flex flex-col items-center justify-center">
                              <div className="relative mb-8">
                                <div className="w-24 h-24 rounded-full border-4 border-orange-100 dark:border-orange-900/30 flex items-center justify-center shadow-md shadow-orange-500/10">
                                  <motion.div
                                    animate={{
                                      rotate: 360,
                                    }}
                                    transition={{
                                      duration: 3,
                                      repeat: Infinity,
                                      ease: "linear",
                                    }}
                                    className="absolute inset-0"
                                  >
                                    <svg
                                      className="w-full h-full"
                                      viewBox="0 0 100 100"
                                    >
                                      <circle
                                        cx="50"
                                        cy="50"
                                        r="40"
                                        stroke="currentColor"
                                        strokeWidth="8"
                                        strokeLinecap="round"
                                        strokeDasharray="62.83 188.5"
                                        fill="transparent"
                                        className="text-orange-500"
                                      />
                                    </svg>
                                  </motion.div>
                                  <BrainCircuit className="h-10 w-10 text-orange-500" />
                                </div>

                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: [0, 1.2, 1] }}
                                  transition={{
                                    duration: 0.5,
                                    repeat: Infinity,
                                    repeatDelay: 4,
                                  }}
                                  className="absolute top-0 right-0 -mt-2 -mr-2"
                                >
                                  <Sparkles className="h-6 w-6 text-orange-400" />
                                </motion.div>
                              </div>

                              <h4 className="text-xl font-medium text-slate-800 dark:text-slate-200 mb-2">
                                Reentrenando IA
                              </h4>

                              <p className="text-slate-500 dark:text-slate-400 mb-8">
                                {retrainingStage}
                              </p>

                              <div className="w-full max-w-md mb-4">
                                <div className="h-2.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden shadow-inner">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{
                                      width: `${retrainingProgress}%`,
                                    }}
                                    className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"
                                  />
                                </div>
                                <div className="flex justify-between mt-2.5 text-xs text-slate-500 dark:text-slate-400">
                                  <span>Iniciando</span>
                                  <span>{retrainingProgress}%</span>
                                  <span>Completado</span>
                                </div>
                              </div>

                              <div className="text-center text-slate-400 dark:text-slate-500 text-sm mt-4 max-w-sm">
                                <p>Este proceso puede tardar unos momentos.</p>
                                <p>
                                  Estamos mejorando el modelo de IA según tus
                                  indicaciones.
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        ) : (
                          <div className="space-y-4 flex-1 flex flex-col">
                            <div className="bg-orange-50 dark:bg-orange-900/10 rounded p-4 border border-orange-100/70 dark:border-orange-900/20 shadow-sm">
                              <p className="text-slate-700 dark:text-slate-300">
                                Indique el motivo por el cual considera que el
                                modelo necesita ser reentrenado. Sea lo más
                                específico posible, describiendo qué aspectos
                                funcionan incorrectamente.
                              </p>
                            </div>

                            <div className="mt-2 flex-1">
                              <label
                                htmlFor="retraining-reason"
                                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                              >
                                Motivo del reentrenamiento
                              </label>
                              <div className="relative">
                                <Textarea
                                  id="retraining-reason"
                                  ref={textareaRef}
                                  value={retrainingReason}
                                  onChange={(e) =>
                                    setRetrainingReason(e.target.value)
                                  }
                                  placeholder="Ej: El campo 'fecha de emisión' está siendo incorrectamente extraído del documento..."
                                  className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-slate-800/50 resize-none overflow-hidden"
                                  disabled={isRetraining}
                                  rows={4}
                                />
                              </div>
                            </div>

                            <div className="flex items-center justify-between mt-auto">
                              <Button
                                variant="outline"
                                onClick={toggleRetrainingMode}
                                className="text-slate-700 dark:text-slate-300 rounded-full"
                              >
                                Cancelar
                              </Button>

                              <Button
                                onClick={startRetraining}
                                className="bg-gradient-to-br from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white font-medium rounded-full px-5 shadow-sm shadow-orange-500/20 border-0"
                              >
                                <Target className="h-4 w-4 mr-2" />
                                Iniciar entrenamiento
                              </Button>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="p-4">
                  <div className="flex items-center mb-4 space-x-2">
                    <div className="relative flex-1">
                      {/* <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" /> */}
                      <Input
                        placeholder="Search in extracted data..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="pl-9 rounded-full border-slate-200 dark:border-slate-700 focus:ring-offset-0"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyToClipboard}
                      className="flex-shrink-0 rounded-full"
                    >
                      {copied ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {/* Recuadro explicativo que aparece solo en modo reentrenamiento */}
                  {isRetrainingMode && (
                    <div className="bg-orange-50 dark:bg-orange-900/10 rounded p-4 mb-5 border border-orange-100/70 dark:border-orange-900/20 shadow-sm">
                      <p className="text-slate-700 dark:text-slate-300 font-medium mb-2">
                        Modo de edición y reentrenamiento
                      </p>
                      <div className="flex flex-col gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <div className="flex items-start">
                          <CheckCircle className="h-4 w-4 mr-2 text-slate-500 mt-0.5" />
                          <p>
                            <span className="font-medium">
                              Guardar cambios:
                            </span>{" "}
                            Corrige errores puntuales sin modificar el modelo de
                            IA.
                          </p>
                        </div>
                        <div className="flex items-start">
                          <BrainCircuit className="h-4 w-4 mr-2 text-orange-500 mt-0.5" />
                          <p>
                            <span className="font-medium">
                              Entrenar con cambios:
                            </span>{" "}
                            Actualiza el modelo de IA para mejorar la extracción
                            futura de documentos similares.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {Object.entries(data)
                      .filter(([key]) => key !== "id" && key !== "documentUrl")
                      .map(([key, value]) => {
                        value = formatCellValue
                          ? formatCellValue(value)
                          : value;
                        const displayValue = formatCellValue
                          ? formatCellValue(value)
                          : value;
                        return (
                          <div
                            key={key}
                            className="border-b border-slate-200/70 dark:border-slate-700/50 pb-4 mb-1"
                          >
                            <div className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                              {getColumnLabel(key)}
                            </div>
                            {isRetrainingMode ? (
                              <div className="relative">
                                <Input
                                  value={editedData[key] || ""}
                                  onChange={(e) =>
                                    handleFieldEdit(key, e.target.value)
                                  }
                                  className={`rounded transition-all ${
                                    editedData[key] !== value
                                      ? "border-orange-300 dark:border-orange-600 bg-orange-50 dark:bg-orange-900/20 shadow-sm"
                                      : "border-slate-200 dark:border-slate-700"
                                  }`}
                                />
                                {editedData[key] !== value && (
                                  <div className="absolute right-0 top-0 mt-2.5 mr-3 flex items-center">
                                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-slate-800 dark:text-slate-200 py-2 px-1">
                                {typeof displayValue === "boolean"
                                  ? displayValue
                                    ? "Yes"
                                    : "No"
                                  : typeof displayValue === "string"
                                  ? highlightText(displayValue, searchText)
                                  : String(displayValue)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>

                  {isRetrainingMode && hasEdits && (
                    <div className="mt-8 flex justify-end space-x-3">
                      <Button
                        onClick={handleSaveChanges}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 font-medium rounded-full px-5"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Guardar cambios
                      </Button>

                      <Button
                        onClick={startRetraining}
                        className="bg-gradient-to-br from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white font-medium rounded-full px-5 shadow-sm shadow-orange-500/20 border-0"
                      >
                        <BrainCircuit className="h-4 w-4 mr-2" />
                        Entrenar con cambios
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

